import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
    Sun, IndianRupee, CheckCircle, FileText, Home, Zap, Shield,
    Award, ArrowRight, Search, BadgePercent, Users, Building2,
    Leaf, Calculator, Phone, MapPin, Clock, TrendingUp, Star,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import ExpertVisitModal from '../components/ExpertVisitModal';
import { API } from '../App';
import axios from 'axios';

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '', prefix = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (isInView) {
            const duration = 2000;
            const steps = 60;
            const increment = value / steps;
            let current = 0;
            const timer = setInterval(() => {
                current += increment;
                if (current >= value) {
                    setCount(value);
                    clearInterval(timer);
                } else {
                    setCount(Math.floor(current));
                }
            }, duration / steps);
            return () => clearInterval(timer);
        }
    }, [isInView, value]);

    return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const SubsidyPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('featured');
    const [slabIndex, setSlabIndex] = useState(0);
    const [swipeDir, setSwipeDir] = useState(1);
    const [showExpertModal, setShowExpertModal] = useState(false);
    const touchStartX = useRef(0);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            if (diff > 0 && slabIndex < subsidySlabs.length - 1) {
                setSwipeDir(1);
                setSlabIndex(prev => prev + 1);
            } else if (diff < 0 && slabIndex > 0) {
                setSwipeDir(-1);
                setSlabIndex(prev => prev - 1);
            }
        }
    };

    useEffect(() => {
        fetchSubsidyProducts();
    }, []);

    const fetchSubsidyProducts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/api/products/subsidy-eligible`);
            setProducts(response.data);
        } catch (error) {
            console.error('Failed to fetch subsidy products:', error);
            setProducts([]);
        }
        setLoading(false);
    };

    const subsidySlabs = [
        { range: 'Up to 2 kW', subsidy: '₹30,000/kW', maxAmount: '₹60,000', benefit: 'Best for 2-3 BHK', icon: Home, gradient: 'from-emerald-500 to-green-600' },
        { range: '2 kW to 3 kW', subsidy: '₹18,000/kW extra', maxAmount: '₹78,000', benefit: 'Best for 3-4 BHK', icon: Building2, gradient: 'from-blue-500 to-indigo-600' },
        { range: 'Above 3 kW', subsidy: 'Fixed Amount', maxAmount: '₹78,000', benefit: 'Best for Villas', icon: Award, gradient: 'from-purple-500 to-violet-600' }
    ];

    const steps = [
        { step: 1, title: 'Register Online', desc: 'Apply on PM Surya Ghar portal with Aadhaar', icon: FileText, color: 'bg-blue-500' },
        { step: 2, title: 'Choose Vendor', desc: 'Select from empanelled solar vendors like us', icon: Users, color: 'bg-green-500' },
        { step: 3, title: 'Installation', desc: 'Get your rooftop solar system installed', icon: Zap, color: 'bg-amber-500' },
        { step: 4, title: 'Net Metering', desc: 'DISCOM inspects and approves connection', icon: Shield, color: 'bg-purple-500' },
        { step: 5, title: 'Subsidy Credit', desc: 'Receive subsidy directly in bank account', icon: IndianRupee, color: 'bg-emerald-500' }
    ];

    const benefits = [
        { title: 'Free Electricity', desc: '300 units free every month', icon: Zap, value: '300', suffix: ' Units/Month' },
        { title: 'Government Subsidy', desc: 'Direct benefit transfer', icon: IndianRupee, value: '78000', prefix: '₹', suffix: ' Max' },
        { title: 'Bill Reduction', desc: 'Reduce electricity bills', icon: TrendingUp, value: '70', suffix: '%' },
        { title: 'Green Energy', desc: 'Reduce carbon footprint', icon: Leaf, value: '25', suffix: ' Years Warranty' }
    ];

    const filteredProducts = products.filter(p =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen">
            {/* Hero Banner with Full Background Image */}
            <div className="relative min-h-[55vh] md:min-h-[85vh] flex items-center overflow-hidden subsidy-hero-banner md:mt-0 md:pt-0">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80"
                        alt="Solar Panels"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
                </div>

                {/* Floating Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <motion.div
                        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
                        transition={{ duration: 6, repeat: Infinity }}
                        className="absolute top-1/4 right-1/4 w-32 h-32 bg-amber-400/20 rounded-full blur-xl"
                    />
                    <motion.div
                        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
                        transition={{ duration: 8, repeat: Infinity }}
                        className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-orange-400/20 rounded-full blur-2xl"
                    />
                </div>

                <div className="container-solar relative z-10 pt-16 md:pt-24 pb-20 md:pb-0">
                    <div className="max-w-3xl">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {/* Government Badge */}
                            <div className="inline-flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 md:py-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-4 md:mb-8">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/5/55/Emblem_of_India.svg"
                                    alt="India Emblem"
                                    className="w-6 h-6 md:w-8 md:h-8"
                                />
                                <span className="text-white font-medium text-xs md:text-base">Govt. of India Initiative</span>
                                <Badge className="bg-green-500/90 text-white border-0 text-xs">Official</Badge>
                            </div>

                            <h1 className="text-3xl md:text-6xl lg:text-7xl font-bold text-white mb-3 md:mb-6 leading-tight">
                                PM Surya Ghar
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400">
                                    Muft Bijli Yojana
                                </span>
                            </h1>

                            <p className="text-sm md:text-2xl text-gray-300 mb-4 md:mb-8 leading-relaxed max-w-2xl">
                                Get <span className="text-amber-400 font-bold">₹78,000 subsidy</span> from the Central Government
                                and enjoy <span className="text-green-400 font-bold">300 units free electricity</span> every month.
                            </p>

                            {/* Quick Stats */}
                            <div className="flex flex-wrap gap-3 md:gap-6 mb-5 md:mb-10">
                                <div className="flex items-center gap-2 md:gap-3 bg-white/10 backdrop-blur-sm px-3 md:px-5 py-2 md:py-3 rounded-lg md:rounded-xl border border-white/10">
                                    <IndianRupee className="w-4 h-4 md:w-6 md:h-6 text-amber-400" />
                                    <div>
                                        <p className="text-base md:text-2xl font-bold text-white">₹78,000</p>
                                        <p className="text-[10px] md:text-xs text-gray-400">Max Subsidy</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 md:gap-3 bg-white/10 backdrop-blur-sm px-3 md:px-5 py-2 md:py-3 rounded-lg md:rounded-xl border border-white/10">
                                    <Zap className="w-4 h-4 md:w-6 md:h-6 text-green-400" />
                                    <div>
                                        <p className="text-base md:text-2xl font-bold text-white">300 Units</p>
                                        <p className="text-[10px] md:text-xs text-gray-400">Free/Month</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 md:gap-3 bg-white/10 backdrop-blur-sm px-3 md:px-5 py-2 md:py-3 rounded-lg md:rounded-xl border border-white/10">
                                    <Users className="w-4 h-4 md:w-6 md:h-6 text-blue-400" />
                                    <div>
                                        <p className="text-base md:text-2xl font-bold text-white">1 Crore+</p>
                                        <p className="text-[10px] md:text-xs text-gray-400">Enrolled</p>
                                    </div>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-wrap gap-4">
                                <Button
                                    size="lg"
                                    onClick={() => setShowExpertModal(true)}
                                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm md:text-lg px-5 md:px-8 py-4 md:py-6 rounded-xl shadow-lg shadow-amber-500/25"
                                >
                                    <Sun className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                                    Book Free Expert Visit
                                </Button>
                                <Link to="/calculator">
                                    <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 text-sm md:text-lg px-5 md:px-8 py-4 md:py-6 rounded-xl backdrop-blur-sm">
                                        <Calculator className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                                        Calculate Savings
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
                >
                    <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
                        <div className="w-1.5 h-3 bg-white/60 rounded-full" />
                    </div>
                </motion.div>
            </div>

            {/* Scheme Benefits - Statistics Section */}
            <section className="py-10 md:py-20 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl" />
                </div>

                <div className="container-solar relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-8 md:mb-16"
                    >
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-2 md:mb-4">Why PM Surya Ghar?</Badge>
                        <h2 className="text-2xl md:text-5xl font-bold text-white mb-2 md:mb-4">
                            Benefits at a Glance
                        </h2>
                        <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-lg">
                            Transform your home into a power generator with government support
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative group"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-8 text-center hover:border-amber-500/50 transition-colors h-full flex flex-col justify-center">
                                    <div className="w-10 h-10 md:w-16 md:h-16 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-6">
                                        <benefit.icon className="w-5 h-5 md:w-8 md:h-8 text-amber-400" />
                                    </div>
                                    <p className="text-xl md:text-4xl font-bold text-white mb-1 md:mb-2">
                                        <AnimatedCounter value={parseInt(benefit.value)} prefix={benefit.prefix || ''} suffix={benefit.suffix || ''} />
                                    </p>
                                    <h3 className="text-sm md:text-lg font-semibold text-white mb-0.5 md:mb-1">{benefit.title}</h3>
                                    <p className="text-gray-400 text-[11px] md:text-sm">{benefit.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Subsidy Slabs - Visual Cards */}
            <section className="py-10 md:py-20 bg-background">
                <div className="container-solar">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-6 md:mb-16"
                    >
                        <Badge className="bg-primary/10 text-primary border-primary/20 mb-2 md:mb-4">Subsidy Structure</Badge>
                        <h2 className="text-2xl md:text-5xl font-bold mb-2 md:mb-4">
                            How Much Subsidy Can You Get?
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-lg">
                            Subsidy based on system capacity. Bigger systems, bigger savings!
                        </p>
                    </motion.div>

                    {/* Desktop: Grid */}
                    <div className="hidden md:grid md:grid-cols-3 gap-8">
                        {subsidySlabs.map((slab, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.15 }}
                                className="group relative"
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br ${slab.gradient} rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity`} />
                                <div className="relative bg-card border rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                                    <div className={`bg-gradient-to-br ${slab.gradient} p-8 text-white`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <slab.icon className="w-12 h-12 opacity-80" />
                                            {index === 1 && (
                                                <Badge className="bg-white/20 text-white border-0">Most Popular</Badge>
                                            )}
                                        </div>
                                        <h3 className="text-2xl font-bold mb-1">{slab.range}</h3>
                                        <p className="text-white/80">{slab.benefit}</p>
                                    </div>
                                    <div className="p-8">
                                        <div className="mb-6">
                                            <p className="text-muted-foreground text-sm mb-2">Subsidy Rate</p>
                                            <p className="text-xl font-semibold">{slab.subsidy}</p>
                                        </div>
                                        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                                            <span className="text-muted-foreground">Maximum Benefit</span>
                                            <span className="text-3xl font-bold text-primary">{slab.maxAmount}</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile: Animated Card Slider with Arrows */}
                    <div
                        className="md:hidden relative"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={slabIndex}
                                initial={{ opacity: 0, x: swipeDir * 60 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: swipeDir * -60 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className="group relative"
                            >
                                {(() => {
                                    const slab = subsidySlabs[slabIndex];
                                    return (
                                        <>
                                            <div className={`absolute inset-0 bg-gradient-to-br ${slab.gradient} rounded-2xl blur-xl opacity-20`} />
                                            <div className="relative bg-card border rounded-2xl overflow-hidden">
                                                <div className={`bg-gradient-to-br ${slab.gradient} p-5 text-white`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <slab.icon className="w-10 h-10 opacity-80" />
                                                        {slabIndex === 1 && (
                                                            <Badge className="bg-white/20 text-white border-0 text-xs">Most Popular</Badge>
                                                        )}
                                                    </div>
                                                    <h3 className="text-xl font-bold mb-0.5">{slab.range}</h3>
                                                    <p className="text-white/80 text-sm">{slab.benefit}</p>
                                                </div>
                                                <div className="p-5">
                                                    <div className="mb-4">
                                                        <p className="text-muted-foreground text-xs mb-1">Subsidy Rate</p>
                                                        <p className="text-lg font-semibold">{slab.subsidy}</p>
                                                    </div>
                                                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                                                        <span className="text-muted-foreground text-sm">Maximum Benefit</span>
                                                        <span className="text-2xl font-bold text-primary">{slab.maxAmount}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </motion.div>
                        </AnimatePresence>

                        {/* Arrow Buttons */}
                        <div className="flex items-center justify-center gap-4 mt-4">
                            <button
                                onClick={() => { setSwipeDir(-1); setSlabIndex(prev => Math.max(0, prev - 1)); }}
                                disabled={slabIndex === 0}
                                className="w-10 h-10 rounded-full bg-card border flex items-center justify-center disabled:opacity-30 transition-opacity active:scale-95"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>

                            {/* Dots */}
                            <div className="flex items-center gap-2">
                                {subsidySlabs.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSlabIndex(i)}
                                        className={`rounded-full transition-all duration-300 ${i === slabIndex ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-muted-foreground/30'}`}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={() => { setSwipeDir(1); setSlabIndex(prev => Math.min(subsidySlabs.length - 1, prev + 1)); }}
                                disabled={slabIndex === subsidySlabs.length - 1}
                                className="w-10 h-10 rounded-full bg-card border flex items-center justify-center disabled:opacity-30 transition-opacity active:scale-95"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Expert Visit CTA — replaces How to Avail */}
            <section className="py-10 md:py-20 bg-muted/30 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1920&q=60"
                        alt="Solar Installation"
                        className="w-full h-full object-cover opacity-5"
                    />
                </div>

                <div className="container-solar relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/20 mb-4">Zero Hassle</Badge>
                            <h2 className="text-2xl md:text-5xl font-bold mb-4 md:mb-6">
                                How to Avail the Subsidy
                            </h2>
                            <p className="text-muted-foreground text-sm md:text-xl mb-6 md:mb-8 leading-relaxed max-w-2xl mx-auto">
                                Registering on portals, collecting documents, applying for subsidies, picking the right system — <strong>let us handle everything</strong>. Our solar expert will visit your home <em>for free</em>, assess your roof, recommend the perfect system, and take care of the entire subsidy process.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.15 }}
                            className="bg-card border-2 border-primary/20 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-xl"
                        >
                            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/25">
                                    <Sun className="w-8 h-8 md:w-10 md:h-10 text-white" />
                                </div>
                                <div className="text-center md:text-left flex-1">
                                    <h3 className="text-lg md:text-2xl font-bold mb-1.5">Free Home Assessment & Complete Assistance</h3>
                                    <p className="text-muted-foreground text-sm md:text-base">
                                        From roof survey to subsidy credit in your bank — our expert handles <strong>everything</strong>. You just sit back.
                                    </p>
                                </div>
                                <Button
                                    size="lg"
                                    onClick={() => setShowExpertModal(true)}
                                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 md:px-8 py-5 md:py-6 rounded-xl text-sm md:text-lg shrink-0 shadow-lg shadow-amber-500/25"
                                >
                                    <Phone className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                                    Book Free Expert Visit
                                </Button>
                            </div>

                            <div className="grid grid-cols-3 gap-3 md:gap-4 mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border">
                                <div className="text-center">
                                    <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-500 mx-auto mb-1" />
                                    <p className="text-xs md:text-sm font-medium">100% Free</p>
                                </div>
                                <div className="text-center">
                                    <Shield className="w-5 h-5 md:w-6 md:h-6 text-blue-500 mx-auto mb-1" />
                                    <p className="text-xs md:text-sm font-medium">Subsidy Handled</p>
                                </div>
                                <div className="text-center">
                                    <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-500 mx-auto mb-1" />
                                    <p className="text-xs md:text-sm font-medium">Visit in 48hrs</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Eligibility & Documents - Two Column */}
            <section className="py-10 md:py-20 bg-background">
                <div className="container-solar">
                    <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
                        {/* Eligibility */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="flex items-center gap-3 mb-4 md:mb-8">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-500" />
                                </div>
                                <h2 className="text-xl md:text-3xl font-bold">Eligibility Criteria</h2>
                            </div>

                            <div className="space-y-2 md:space-y-4">
                                {[
                                    'Indian citizen with valid Aadhaar card',
                                    'Own residential property with electricity',
                                    'LT domestic consumer category',
                                    'Roof ownership or consent from owner',
                                    'Grid-connected solar system only',
                                    'No previous subsidy availed'
                                ].map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex items-center gap-3 md:gap-4 bg-green-50 dark:bg-green-950/30 p-3 md:p-4 rounded-lg md:rounded-xl border border-green-100 dark:border-green-900"
                                    >
                                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                                        <span className="font-medium text-sm md:text-base">{item}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Documents */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="flex items-center gap-3 mb-4 md:mb-8">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                                    <FileText className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
                                </div>
                                <h2 className="text-xl md:text-3xl font-bold">Required Documents</h2>
                            </div>

                            <div className="grid grid-cols-2 gap-2 md:gap-4">
                                {[
                                    { name: 'Aadhaar Card', desc: 'Linked to mobile', required: true },
                                    { name: 'Electricity Bill', desc: 'Recent bill copy', required: true },
                                    { name: 'Bank Passbook', desc: 'For subsidy credit', required: true },
                                    { name: 'Property Proof', desc: 'Ownership document', required: true },
                                    { name: 'Passport Photo', desc: 'Recent photograph', required: true },
                                    { name: 'PAN Card', desc: 'Optional but helpful', required: false }
                                ].map((doc, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.05 }}
                                        className={`p-3 md:p-4 rounded-lg md:rounded-xl border ${doc.required ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900' : 'bg-muted border-border'}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc.required ? 'bg-blue-100 dark:bg-blue-900' : 'bg-muted'}`}>
                                                <FileText className={`w-4 h-4 ${doc.required ? 'text-blue-500' : 'text-muted-foreground'}`} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm md:text-base flex items-center gap-1 md:gap-2">
                                                    {doc.name}
                                                    {doc.required && <span className="text-red-500">*</span>}
                                                </p>
                                                <p className="text-xs md:text-sm text-muted-foreground">{doc.desc}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Eligible Products */}
            <section id="eligible-products" className="py-10 md:py-20 bg-gradient-to-b from-muted/50 to-background">
                <div className="container-solar">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
                    >
                        <div>
                            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">Shop with Subsidy</Badge>
                            <h2 className="text-2xl md:text-4xl font-bold mb-2">Subsidy Eligible Products</h2>
                            <p className="text-muted-foreground">
                                All products below qualify for PM Surya Ghar subsidy
                            </p>
                        </div>
                        <Link to="/shop?subsidy=true">
                            <Button variant="outline" className="group">
                                View All Products
                                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </motion.div>

                    {/* Search */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                placeholder="Search subsidy products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 h-12 rounded-xl"
                            />
                        </div>
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-48 h-12 rounded-xl">
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="featured">Featured</SelectItem>
                                <SelectItem value="price-low">Price: Low to High</SelectItem>
                                <SelectItem value="price-high">Price: High to Low</SelectItem>
                                <SelectItem value="subsidy">Highest Subsidy</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Products Grid */}
                    {loading ? (
                        <div className="text-center py-16">
                            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-muted-foreground">Loading products...</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-16 bg-card rounded-2xl border">
                            <BadgePercent className="w-20 h-20 text-muted-foreground mx-auto mb-6" />
                            <h3 className="font-bold text-2xl mb-3">No Products Found</h3>
                            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                                {searchQuery ? 'Try a different search term' : 'Check back soon for subsidy-eligible products'}
                            </p>
                            <Link to="/shop">
                                <Button size="lg">Browse All Products</Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
                            {filteredProducts.map((product, index) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <ProductCard product={product} showSubsidyPrice={true} />
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-10 md:py-20 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl" />
                </div>

                <div className="container-solar relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center text-white"
                    >
                        <h2 className="text-2xl md:text-5xl font-bold mb-4 md:mb-6">
                            Ready for Free Electricity?
                        </h2>
                        <p className="text-sm md:text-xl text-white/80 mb-6 md:mb-10 max-w-2xl mx-auto">
                            Join lakhs of Indian families saving money and helping the environment with rooftop solar.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link to="/shop?subsidy=true">
                                <Button size="lg" className="bg-white text-amber-600 hover:bg-white/90 text-sm md:text-lg px-5 md:px-8 py-4 md:py-6 rounded-xl shadow-lg">
                                    <BadgePercent className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                                    Shop with Subsidy
                                </Button>
                            </Link>
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={() => setShowExpertModal(true)}
                                className="border-2 border-white text-white hover:bg-white/10 text-sm md:text-lg px-5 md:px-8 py-4 md:py-6 rounded-xl"
                            >
                                <Sun className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                                Book Expert Visit
                            </Button>
                        </div>

                        {/* Trust Badges */}
                        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-6 md:mt-12 pt-6 md:pt-12 border-t border-white/20 text-sm md:text-base">
                            <div className="flex items-center gap-2 text-white/80">
                                <Shield className="w-5 h-5" />
                                <span>Government Verified</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/80">
                                <Star className="w-5 h-5" />
                                <span>4.9★ Rating</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/80">
                                <Users className="w-5 h-5" />
                                <span>10,000+ Installations</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
            {/* Expert Visit Popup Modal */}
            <ExpertVisitModal
                isOpen={showExpertModal}
                onClose={() => setShowExpertModal(false)}
            />
        </div>
    );
};

export default SubsidyPage;
